import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  inject
} from '@angular/core';
import { Health, HealthData } from '@awesome-cordova-plugins/health/ngx';
import { Capacitor } from '@capacitor/core';
import { Device, DeviceInfo } from '@capacitor/device';
import { Network } from '@capacitor/network';
import { Platform } from '@ionic/angular';
import { toString } from 'lodash';
import { DateTime } from 'luxon';
import { LocalStorageService } from 'ngx-webstorage';
import {
  Observable,
  Subject,
  catchError,
  combineLatest,
  concatMap,
  debounceTime,
  delay,
  filter,
  forkJoin,
  from,
  map,
  merge,
  of,
  retry,
  switchMap,
  takeUntil,
  tap,
  throwError,
  timer,
  toArray
} from 'rxjs';
import { WearablesIntegrationService } from '@providers/api/wearables-integration/wearables-integration';
import { SessionList } from '@providers/session/session.interface';
import { EventService } from '../../../providers/event/event.service';
import { Session } from '../../../providers/session/session.service';
import { Logger } from '../../../utils/logger';
import { TYPES } from '../tracker.interface';
import { TrackerSyncService } from './sync.service';

/**
 * Tracker Sync.
 *
 * @exports
 * @class TrackerSyncComponent
 */
@Component({
  selector: 'app-tracker-sync',
  template: ``,
  imports: [],
  providers: [Health, TrackerSyncService, WearablesIntegrationService]
})
export class TrackerSyncComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly events = inject(EventService);
  private readonly health = inject(Health);
  private readonly platform = inject(Platform);
  private readonly session = inject(Session);
  private readonly storage = inject(LocalStorageService);
  private readonly wearablesIntegration = inject(WearablesIntegrationService);

  // State
  private backwardDaysProcessed = 0;
  private forwardDaysProcessed = 0;
  private isAppInBackground = false;
  private isBackwardSyncing = false;
  private isForwardSyncing = false;
  private isNetworkAvailable = true;
  private isSyncing = false;
  private readonly logger = Logger.getInstance('TrackerSyncComponent');

  private readonly RETRY_COUNT = 2;
  private readonly RETRY_DELAY = 45 * 1000; // 45 seconds retry delay
  private retryTimerSubscription: any = null;

  // Sync configuration
  private readonly SYNC_DAYS_INTERVAL = 20 * 1000; // 20 seconds between days
  private readonly SYNC_INIT_INTERVAL = 1 * 60 * 1000; // 1 minute initial delay
  private readonly SYNC_INTERVAL = 10 * 1000; // 10 seconds initial delay
  private readonly SYNC_TODAY_INTERVAL_GAP = 5 * 60 * 1000; // 3 minutes gap to prevent frequent sync in
  device?: DeviceInfo;
  deviceId = 'unknown';

  // Observables for pause/resume
  private readonly destroy$ = new Subject<void>();
  private readonly pauseSync$ = new Subject<void>();
  private readonly resumeSync$ = new Subject<void>();
  private readonly startSyncWithDelaySubject$ = new Subject<void>();

  /**
   * @description Init.
   */
  ngOnInit(): void {
    this.setupNetworkMonitoring();
    this.setupStartSyncDebounce();
  }

  /**
   * @description Init.
   * @memberof TrackerSyncComponent
   */
  ngAfterViewInit(): void {
    // Listen for permission changes
    this.storage
      .observe('permission-health-tracker-types')
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        this.logger.debug('permission-health-tracker-types changed', {
          response
        });
        const data = response as
          | SessionList['permission-health-tracker-types']
          | undefined;
        if (data && data.length > 0) {
          const types = data
            .filter((v) => v.status === 'granted')
            .map((v) => v.type);
          if (types.length > 0) {
            this.logger.info(
              'Health permissions granted, starting sync with delay'
            );
            this.startSyncWithDelay();
          }
        }
      });

    // Listen for profile switch or login
    merge(
      this.events.listen(this.events.keys.PROFILE_SWITCHED),
      this.events.listen(this.events.keys.LOGIN)
    )
      .pipe(debounceTime(1000), takeUntil(this.destroy$))
      .subscribe(() => {
        this.logger.debug('Profile switched or login, starting sync');
        this.stopSync();
        this.startSyncWithDelay();
      });

    // Listen for logout to stop sync
    this.events
      .listen(this.events.keys.LOGOUT)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.stopSync();
      });

    // Handle app pause/resume
    this.platform.pause.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.logger.debug('App paused, pausing sync');
      this.pauseSync();
    });

    this.platform.resume.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.logger.debug('App resumed, resuming sync');
      this.resumeSync();
    });

    this.platform
      .ready()
      .then(() => {
        this.startSyncWithDelay();
      })
      .catch(() => false);
  }

  /**
   * Cleanup subscriptions on destroy.
   */
  ngOnDestroy(): void {
    this.cancelRetryTimer();
    this.destroy$.next();
    this.destroy$.complete();
    this.pauseSync$.next();
    this.pauseSync$.complete();
  }

  /**
   * @description Cancel any pending retry timer.
   * @memberof TrackerSyncComponent
   */
  private cancelRetryTimer(): void {
    if (this.retryTimerSubscription) {
      this.logger.debug('⏹️  Cancelling pending retry timer');
      this.retryTimerSubscription.unsubscribe();
      this.retryTimerSubscription = null;
    }
  }

  /**
   * @description Check and start sync if conditions are met.
   * @memberof TrackerSyncComponent
   */
  private checkAndStartSync(): void {
    if (!this.session.isAuthenticated()) {
      this.logger.debug('User not authenticated, skipping sync');
      return;
    }

    if (this.isSyncing) {
      this.logger.debug('Sync already running, skipping');
      return;
    }

    this.startSyncWithDelay();
  }

  /**
   * @description Get source name based on platform.
   * @returns {string}
   * @memberof TrackerSyncComponent
   */
  private getSourceName(): string {
    if (!this.device) return 'unknown';
    if (/ios/i.test(this.device.platform)) return 'apple_health_kit';
    if (/android/i.test(this.device.platform)) return 'android_google_fit';
    return 'unknown';
  }

  /**
   * @description Get sync progress from API.
   * @returns {Observable<{ backfillDays: number; earliestSynchronizedAt: string | null; latestSynchronizedAt: string | null; wearableCode: string } | null>}
   * @memberof TrackerSyncComponent
   */
  private getSyncProgress(): Observable<{
    backfillDays: number;
    earliestSynchronizedAt: string | null;
    latestSynchronizedAt: string | null;
    wearableCode: string;
  } | null> {
    const wearableCode = this.getWearableCode();
    if (!wearableCode) {
      return throwError(() => new Error('No wearable code available'));
    }

    return this.wearablesIntegration
      .getSyncHealthAppProgress({ wearableCode })
      .pipe(
        map((response) => response.data),
        tap((data) => {
          this.logger.debug('Sync progress retrieved', data);
        })
      );
  }

  /**
   * @description Get wearable code based on platform.
   * @returns {string | null}
   * @memberof TrackerSyncComponent
   */
  private getWearableCode(): string | null {
    if (!this.device) return null;
    if (/ios/i.test(this.device.platform)) return 'HEALTH_KIT';
    if (/android/i.test(this.device.platform)) return 'GOOGLE_FIT';
    return null;
  }

  /**
   * @description Pause sync process.
   * @memberof TrackerSyncComponent
   */
  private pauseSync(): void {
    this.logger.info('🛑 Pausing sync - App going to background');
    this.isAppInBackground = true;
    this.isSyncing = false;
    this.pauseSync$.next();
    this.cancelRetryTimer();
  }

  /**
   * @description Resume sync process.
   * @memberof TrackerSyncComponent
   */
  private resumeSync(): void {
    this.logger.info('▶️  Resuming sync - App coming to foreground');
    this.isAppInBackground = false;
    this.resumeSync$.next();

    // Always try to start sync when app comes to foreground
    if (this.session.isAuthenticated() && this.isNetworkAvailable) {
      if (!this.isSyncing) {
        this.logger.info('🔄 Starting sync after app resume');
        this.scheduleRetry('App resumed', this.SYNC_INIT_INTERVAL);
      } else {
        this.logger.info('ℹ️  Sync already running, will continue');
      }
    } else {
      if (!this.session.isAuthenticated()) {
        this.logger.warn('⚠️  Cannot resume: User not authenticated');
      }
      if (!this.isNetworkAvailable) {
        this.logger.warn('⚠️  Cannot resume: Network not available');
      }
    }
  }

  /**
   * @description Run bidirectional sync (forward and backward).
   * @returns {Observable<boolean>}
   * @memberof TrackerSyncComponent
   */
  private runBidirectionalSync(): Observable<boolean> {
    this.logger.info('=== Starting bidirectional sync ===');

    return forkJoin([this.startForwardSync(), this.startBackwardSync()]).pipe(
      map((results) => {
        // Return true if either forward or backward sync processed data
        const [forwardResult, backwardResult] = results;
        this.logger.info(
          `=== Bidirectional sync results: Forward=${forwardResult}, Backward=${backwardResult} ===`
        );
        return forwardResult || backwardResult;
      }),
      takeUntil(this.pauseSync$),
      catchError((error) => {
        this.logger.error('Bidirectional sync failed', { error });
        return of(false);
      })
    );
  }

  /**
   * @description Schedule a retry attempt after INITIAL_SYNC_DELAY.
   * @param {string} reason Reason for scheduling retry.
   * @param {number} delay Optional delay in milliseconds. Defaults to SYNC_INTERVAL.
   * @memberof TrackerSyncComponent
   */
  private scheduleRetry(reason: string, delay?: number): void {
    this.cancelRetryTimer();

    const retryDelay = delay ?? this.SYNC_INTERVAL;

    this.logger.info(
      `⏰ Scheduling retry in ${retryDelay / 1000}s (Reason: ${reason})`
    );

    this.retryTimerSubscription = timer(retryDelay)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.logger.info('⏱️  Retry timer elapsed, checking conditions...');
        this.retryTimerSubscription = null;

        // Check all conditions before retrying
        if (!this.session.isAuthenticated()) {
          this.logger.warn('❌ Cannot retry: User not authenticated');
          return;
        }

        if (this.isAppInBackground) {
          this.logger.warn('❌ Cannot retry: App in background');
          return;
        }

        if (!this.isNetworkAvailable) {
          this.logger.warn(
            '🌐 Cannot retry: Network not available, will retry when connected'
          );
          return;
        }

        if (!Capacitor.isNativePlatform()) {
          this.logger.warn('❌ Cannot retry: Not on native platform');
          return;
        }

        this.logger.info('✅ All conditions met, starting sync');
        this.startNewSync();
      });
  }

  /**
   * @description Setup network monitoring.
   * @memberof TrackerSyncComponent
   */
  private setupNetworkMonitoring(): void {
    if (!Capacitor.isNativePlatform()) {
      this.logger.debug('Not on native platform, skipping network monitoring');
      return;
    }

    // Check initial network status
    Network.getStatus()
      .then((status) => {
        this.isNetworkAvailable = status.connected;
        this.logger.info(
          `🌐 Initial network status: ${status.connected ? 'Connected' : 'Disconnected'}`
        );
      })
      .catch((error: unknown) => {
        this.logger.error('Failed to get network status', { error });
        this.isNetworkAvailable = true; // Assume available if check fails
      });

    // Listen for network changes
    Network.addListener('networkStatusChange', (status) => {
      const wasAvailable = this.isNetworkAvailable;
      this.isNetworkAvailable = status.connected;

      if (status.connected && !wasAvailable) {
        this.logger.info('🌐 Network reconnected, attempting to resume sync');
        // Network came back, try to start sync if conditions are met
        if (
          this.session.isAuthenticated() &&
          !this.isAppInBackground &&
          !this.isSyncing
        ) {
          this.scheduleRetry('Network reconnected', this.SYNC_INIT_INTERVAL);
        }
      } else if (!status.connected && wasAvailable) {
        this.logger.warn('🌐 Network disconnected, sync will pause');
        // Network lost - sync operations will fail naturally and retry
      }
    });
  }

  /**
   * @description Setup debounced start sync.
   * @memberof TrackerSyncComponent
   */
  private setupStartSyncDebounce(): void {
    this.startSyncWithDelaySubject$
      .pipe(
        filter(() => {
          return Capacitor.isNativePlatform() && this.session.isAuthenticated();
        }),
        tap(() => {
          this.logger.info('Receive new start sync request, debouncing...');
        }),
        debounceTime(this.SYNC_INIT_INTERVAL), // 1 minute debounce
        tap(() => {
          this.logger.info(`Starting to sync`);
        }),
        switchMap(() => timer(this.SYNC_INTERVAL)),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.startNewSync();
      });
  }

  /**
   * @description Start backward sync (current to past).
   * @returns {Observable<boolean>}
   * @memberof TrackerSyncComponent
   */
  private startBackwardSync(): Observable<boolean> {
    if (this.isBackwardSyncing) {
      this.logger.debug('Backward sync already running, skipping');
      return of(false);
    }

    this.isBackwardSyncing = true;
    this.backwardDaysProcessed = 0;
    this.logger.info('>>> Starting BACKWARD sync <<<');

    return this.getSyncProgress().pipe(
      concatMap((progress) => {
        if (!progress) {
          this.logger.warn('No sync progress data, skipping backward sync');
          return of(false);
        }

        this.logger.debug('Backward sync progress:', {
          backfillDays: progress.backfillDays,
          earliestSynchronizedAt: progress.earliestSynchronizedAt,
          wearableCode: progress.wearableCode
        });

        const backfillDays = progress.backfillDays;
        const yesterday = DateTime.now().minus({ days: 1 });
        const targetDate = DateTime.now().minus({ days: backfillDays });

        // If earliestSynchronizedAt is null, sync from yesterday to target
        if (!progress.earliestSynchronizedAt) {
          const from = yesterday;
          const to = yesterday.startOf('day');
          this.logger.info(
            `Backward sync: No earliestSynchronizedAt, syncing from ${from.toISO()} to ${to.toISO()}`
          );
          return this.syncBackwardDays(
            yesterday.startOf('day'),
            yesterday.endOf('day')
          );
        }

        const earliestSyncDate = DateTime.fromISO(
          progress.earliestSynchronizedAt
        );

        // Check if we've reached the target (backfill complete)
        if (earliestSyncDate <= targetDate) {
          this.logger.info(
            `Backward sync: Backfill complete. Earliest ${earliestSyncDate.toFormat('yyyy-MM-dd')} <= Target ${targetDate.toFormat('yyyy-MM-dd')}`
          );
          return of(false);
        }

        // Sync from earliestSyncDate going backwards to target
        this.logger.info(
          `Backward sync: Syncing from ${earliestSyncDate.toFormat('yyyy-MM-dd')} to ${targetDate.toFormat('yyyy-MM-dd')}`
        );

        const prevDay = earliestSyncDate.minus({ days: 1 });
        return this.syncBackwardDays(prevDay, earliestSyncDate).pipe(
          map(() => {
            const hasMoreWork =
              earliestSyncDate.minus({ days: 1 }) > targetDate;
            return hasMoreWork;
          })
        );
      }),
      tap((hasWork) => {
        this.logger.info(
          `<<< BACKWARD sync completed: Processed ${this.backwardDaysProcessed} day(s), Has more work: ${hasWork} <<<`
        );
        this.isBackwardSyncing = false;
      }),
      takeUntil(this.pauseSync$),
      catchError((error) => {
        this.logger.error('Backward sync failed', { error });
        this.isBackwardSyncing = false;
        return of(false);
      })
    );
  }

  /**
   * @description Start forward sync (current to future).
   * @returns {Observable<boolean>}
   * @memberof TrackerSyncComponent
   */
  private startForwardSync(): Observable<boolean> {
    if (this.isForwardSyncing) {
      this.logger.debug('Forward sync already running, skipping');
      return of(false);
    }

    this.isForwardSyncing = true;
    this.forwardDaysProcessed = 0;
    this.logger.info('>>> Starting FORWARD sync <<<');

    return this.getSyncProgress().pipe(
      concatMap((progress) => {
        if (!progress) {
          this.logger.warn('No sync progress data, skipping forward sync');
          return of(false);
        }

        this.logger.debug('Forward sync progress:', {
          backfillDays: progress.backfillDays,
          latestSynchronizedAt: progress.latestSynchronizedAt,
          wearableCode: progress.wearableCode
        });

        const now = DateTime.now();

        // If latestSynchronizedAt is null, sync from current day only
        if (!progress.latestSynchronizedAt) {
          this.logger.info(
            `Forward sync: No latestSynchronizedAt, syncing current day ${now.toFormat('yyyy-MM-dd')}`
          );
          return this.syncForwardDays(now.startOf('day'), now);
        }

        const latestSyncDate = DateTime.fromISO(progress.latestSynchronizedAt);

        // Check if there's a gap larger than backfillDays
        const daysSinceLastSync = now.diff(latestSyncDate, 'days').days;
        if (daysSinceLastSync > progress.backfillDays) {
          // Calculate start date as now minus backfillDays
          let gapStartDate: DateTime = now.minus({
            days: progress.backfillDays
          });

          // Ensure we don't go earlier than earliestSynchronizedAt
          if (progress.earliestSynchronizedAt) {
            const earliestSyncDate = DateTime.fromISO(
              progress.earliestSynchronizedAt
            );
            if (gapStartDate < earliestSyncDate) {
              gapStartDate = earliestSyncDate.startOf('day').plus({ days: 1 });
            }
          }

          this.logger.info(
            `Forward sync: Gap too large (${daysSinceLastSync.toFixed(1)} days > ${progress.backfillDays} backfill days). Starting from ${gapStartDate.toFormat('yyyy-MM-dd')}.`
          );
          return this.syncForwardDays(
            gapStartDate.startOf('day'),
            gapStartDate.endOf('day')
          );
        }

        // If now <= latestSyncDate, we're up to date
        if (now <= latestSyncDate) {
          this.logger.info(
            `Forward sync: Already up to date. Now ${now.toFormat('yyyy-MM-dd')} <= Latest ${latestSyncDate.toFormat('yyyy-MM-dd')}`
          );
          return of(false);
        }
        const minutesDiff = now.diff(latestSyncDate, 'minutes').minutes;
        if (
          latestSyncDate.hasSame(now, 'day') &&
          now <
            latestSyncDate.plus({ milliseconds: this.SYNC_TODAY_INTERVAL_GAP })
        ) {
          this.logger.info(
            `Forward sync: Skipping today's sync - last sync was ${minutesDiff.toFixed(1)} minutes ago at ${latestSyncDate.toFormat('yyyy-MM-dd HH:mm:ss')}. Recent enough (threshold: ${this.SYNC_TODAY_INTERVAL_GAP / (1000 * 60)} minutes).`
          );
          return of(false);
        }

        // Sync from latestSyncDate to now
        this.logger.info(
          `Forward sync: Syncing from ${latestSyncDate.toFormat('yyyy-MM-dd')} to ${now.toFormat('yyyy-MM-dd')}`
        );

        // Calculate the next day to sync
        let nextDay = latestSyncDate.plus({ days: 1 });
        if (nextDay.hasSame(now, 'day')) {
          nextDay = now;
        }

        return this.syncForwardDays(latestSyncDate, nextDay).pipe(
          map(() => {
            // Check if there are more days to sync after this one
            const hasMoreWork = nextDay.startOf('day') < now.startOf('day');
            return hasMoreWork;
          })
        );
      }),
      tap((hasWork) => {
        this.logger.info(
          `<<< FORWARD sync completed: Processed ${this.forwardDaysProcessed} day(s), Has more work: ${hasWork} <<<`
        );
        this.isForwardSyncing = false;
      }),
      takeUntil(this.pauseSync$),
      catchError((error) => {
        this.logger.error('Forward sync failed', { error });
        this.isForwardSyncing = false;
        return of(false);
      })
    );
  }

  /**
   * @description Start new sync process.
   * @memberof TrackerSyncComponent
   */
  private startNewSync(): void {
    if (this.isSyncing) {
      this.logger.debug('Sync already running, cannot start new sync');
      return;
    }

    // Check if running on native platform
    if (!Capacitor.isNativePlatform()) {
      this.logger.warn('Not running on native platform, skipping sync');
      return;
    }

    // Check network availability
    if (!this.isNetworkAvailable) {
      this.logger.warn('🌐 Network not available, will retry when connected');
      this.scheduleRetry('Network unavailable', this.SYNC_INIT_INTERVAL);
      return;
    }

    // Check if app is in background
    if (this.isAppInBackground) {
      this.logger.warn(
        '📱 App is in background, will retry when app is active'
      );
      return;
    }

    this.isSyncing = true;
    this.cancelRetryTimer();
    this.logger.info('========================================');
    this.logger.info('🚀 STARTING NEW SYNC PROCESS');
    this.logger.info('========================================');

    // Check health authorization before starting sync
    from(this.health.isAuthorized(TYPES))
      .pipe(
        concatMap((isAuthorized) => {
          if (!isAuthorized) {
            this.logger.warn('🔒 Health data not authorized, skipping sync');
            this.isSyncing = false;
            return throwError(() => new Error('Health data not authorized'));
          }
          this.logger.debug(
            '✅ Health authorization confirmed, proceeding with sync'
          );
          return this.initializeSync();
        }),
        concatMap(() => this.runBidirectionalSync()),
        takeUntil(this.destroy$),
        takeUntil(this.pauseSync$)
      )
      .subscribe({
        /**
         * @description Sync completed successfully.
         * @param {boolean} hasMoreWork Whether more work is available.
         */
        next: (hasMoreWork) => {
          this.logger.info('✅ Sync process completed successfully');
          this.logger.info(`Has more work to do: ${hasMoreWork}`);
          this.isSyncing = false;

          // Always schedule next sync to keep checking for new data
          if (
            this.session.isAuthenticated() &&
            !this.isAppInBackground &&
            this.isNetworkAvailable
          ) {
            if (hasMoreWork) {
              this.logger.info(
                `⏰ More days to sync detected, scheduling next iteration in ${this.SYNC_DAYS_INTERVAL / 1000} seconds`
              );
              this.scheduleRetry('More work available');
            } else {
              this.logger.info(
                '🎉 All sync work completed! Scheduling next check to monitor for new data...'
              );
              this.scheduleRetry(
                'Continuous monitoring - checking for new data',
                this.SYNC_INIT_INTERVAL
              );
            }
          } else {
            this.logger.info(
              '⚠️  Sync stopped: App conditions changed (logged out, backgrounded, or network unavailable)'
            );
          }
        },
        /**
         * @description Sync failed.
         * @param {unknown} error Error.
         */
        error: (error) => {
          this.logger.error('❌ Sync process failed', { error });
          this.isSyncing = false;

          // Don't retry for authorization errors
          if (error?.message === 'Health data not authorized') {
            this.logger.warn('⚠️  Not retrying: Health data not authorized');
            return;
          }

          // Always schedule retry on error (network, API errors, etc.)
          if (this.session.isAuthenticated()) {
            this.logger.info(
              '🔄 Error occurred, scheduling automatic retry in 60 seconds'
            );
            this.scheduleRetry('Error occurred');
          } else {
            this.logger.warn('⚠️  Not retrying: User logged out');
          }
        }
      });
  }

  /**
   * @description Start sync with initial delay (debounced).
   * @memberof TrackerSyncComponent
   */
  private startSyncWithDelay(): void {
    this.startSyncWithDelaySubject$.next();
  }

  /**
   * @description Sync backward days.
   * @param {DateTime} startDate Start date (earliest).
   * @param {DateTime} endDate End date (target).
   * @returns {Observable<boolean>}
   * @memberof TrackerSyncComponent
   */
  private syncBackwardDays(
    startDate: DateTime,
    endDate: DateTime
  ): Observable<boolean> {
    this.logger.info(`⬅️  Starting backward sync with parameters:`, {
      startDate: startDate.toISO(),
      endDate: endDate.toISO(),
      startDateFormatted: startDate.toFormat('yyyy-MM-dd HH:mm:ss'),
      endDateFormatted: endDate.toFormat('yyyy-MM-dd HH:mm:ss')
    });

    return from([
      {
        start: startDate,
        end: endDate
      }
    ]).pipe(
      concatMap((day) => {
        this.logger.info(
          `Processing backward day: ${day.start.toISO()} to ${day.end.toISO()}`
        );
        return this.syncOneDayAllTypes(day.start, day.end).pipe(
          tap((success) => {
            if (success) {
              this.backwardDaysProcessed++;
              this.logger.info(
                `✅ Day ${day.start.toFormat('yyyy-MM-dd')} synced successfully (${this.backwardDaysProcessed} total)`
              );
            } else {
              this.logger.warn(
                `⚠️  Day ${day.start.toFormat('yyyy-MM-dd')} sync failed or skipped`
              );
            }
          })
        );
      }),
      takeUntil(this.pauseSync$),
      map(() => true)
    );
  }

  /**
   * @description Sync forward days.
   * @param {DateTime} startDate Start date.
   * @param {DateTime} endDate End date.
   * @returns {Observable<boolean>}
   * @memberof TrackerSyncComponent
   */
  private syncForwardDays(
    startDate: DateTime,
    endDate: DateTime
  ): Observable<boolean> {
    this.logger.info(`➡️  Starting forward sync with parameters:`, {
      startDate: startDate.toISO(),
      endDate: endDate.toISO(),
      startDateFormatted: startDate.toFormat('yyyy-MM-dd HH:mm:ss'),
      endDateFormatted: endDate.toFormat('yyyy-MM-dd HH:mm:ss')
    });

    return from([
      {
        start: startDate,
        end: endDate
      }
    ]).pipe(
      concatMap((day) => {
        this.logger.info(
          `➡️  Processing forward day: ${day.start.toISO()} to ${day.end.toISO()}`
        );
        return this.syncOneDayAllTypes(day.start, day.end).pipe(
          tap((success) => {
            if (success) {
              this.forwardDaysProcessed++;
              this.logger.info(
                `✅ Day ${day.start.toFormat('yyyy-MM-dd')} synced successfully (${this.forwardDaysProcessed} total)`
              );
            } else {
              this.logger.warn(
                `⚠️  Day ${day.start.toFormat('yyyy-MM-dd')} sync failed or skipped`
              );
            }
          })
        );
      }),
      takeUntil(this.pauseSync$),
      map(() => true)
    );
  }

  /**
   * @description Sync one day for all types.
   * @param {DateTime} startDate Start date for sync.
   * @param {DateTime} endDate End date for sync.
   * @returns {Observable<boolean>}
   * @memberof TrackerSyncComponent
   */
  private syncOneDayAllTypes(
    startDate: DateTime,
    endDate: DateTime
  ): Observable<boolean> {
    const now = DateTime.now();
    const adjustedEndDate = endDate > now ? now : endDate;

    this.logger.info(
      `📅 Syncing all types from ${startDate.toFormat('yyyy-MM-dd HH:mm')} to ${adjustedEndDate.toFormat('yyyy-MM-dd HH:mm')}`
    );
    this.logger.debug(`Querying ${TYPES.length} health data types...`);

    // Query all types sequentially to avoid impacting device performance
    return from(TYPES).pipe(
      concatMap((type, index) => {
        this.logger.debug(
          `  [${index + 1}/${TYPES.length}] Querying type: ${type}`
        );
        return this.queryHealthData(
          type,
          startDate.toJSDate(),
          adjustedEndDate.toJSDate()
        ).pipe(
          map((data) => {
            this.logger.debug(`  ✓ ${type}: ${data.length} record(s) found`);
            return { type, data };
          }),
          catchError((error) => {
            this.logger.error(`  ✗ ${type}: Query failed`, { error });
            return of({ type, data: [] });
          }),
          delay(500) // Small delay between each type query
        );
      }),
      // Collect all results into an array
      toArray(),
      concatMap((results: Array<{ type: string; data: Array<HealthData> }>) => {
        // Transform to API payload format
        const metrics = results.map((r) => ({
          code: r.type.toUpperCase(),
          details: r.data.map((d) => ({
            detail: `${r.type} data`,
            endDate: DateTime.fromJSDate(d.endDate).toISO() ?? '',
            name: r.type,
            sourceBundleId: d.sourceBundleId || '',
            sourceName: d.sourceName || '',
            startDate: DateTime.fromJSDate(d.startDate).toISO() ?? '',
            unit: d.unit || '',
            value: this.parseValue(r.type, d.value)
          }))
        }));

        const totalRecords = results.reduce((sum, r) => sum + r.data.length, 0);
        this.logger.info(
          `📊 Query complete: ${totalRecords} total record(s) across ${metrics.length} type(s) with data`
        );

        if (metrics.length === 0) {
          this.logger.debug(
            `No data from ${startDate.toFormat('yyyy-MM-dd')} to ${adjustedEndDate.toFormat('yyyy-MM-dd')}`
          );
        }

        const payload = {
          deviceId: this.deviceId,
          metrics,
          source: this.getSourceName(),
          // add type any because in local lint shows no error but in pipeline shows error
          syncEndDate: adjustedEndDate.toISO() as any as string,
          syncStartDate: startDate.toISO() as any as string
        };

        this.logger.info(
          `📤 Posting ${metrics.length} metric(s) to API from ${startDate.toFormat('yyyy-MM-dd')} to ${adjustedEndDate.toFormat('yyyy-MM-dd')}`
        );

        return this.wearablesIntegration.postSyncHealthAppReceive(payload).pipe(
          map(() => {
            this.logger.info(
              `✅ API call successful from ${startDate.toFormat('yyyy-MM-dd')} to ${adjustedEndDate.toFormat('yyyy-MM-dd')}`
            );
            return true;
          }),
          retry({
            count: this.RETRY_COUNT,
            delay: this.RETRY_DELAY
          }),
          catchError((error) => {
            this.logger.error(
              `❌ Failed to post data from ${startDate.toFormat('yyyy-MM-dd')} to ${adjustedEndDate.toFormat('yyyy-MM-dd')} after ${this.RETRY_COUNT} retries`,
              { error }
            );
            return of(false);
          })
        );
      })
    );
  }

  /**
   * @description Initialize sync prerequisites.
   * @returns {Observable<boolean>}
   * @memberof TrackerSyncComponent
   */
  initializeSync(): Observable<boolean> {
    return of(true).pipe(
      concatMap(() => combineLatest([Device.getInfo(), Device.getId()])),
      tap(([device, deviceId]) => {
        this.device = device;
        this.deviceId = deviceId.identifier;
        this.logger.debug('Device info obtained', {
          platform: device.platform
        });
      }),
      concatMap(() => {
        return this.health.isAuthorized(TYPES);
      }),
      map((authorized) => {
        if (!authorized) {
          throw new Error('health data not authorized');
        }
        this.logger.debug('Health data authorization confirmed');
        return true;
      })
    );
  }

  /**
   * @description Parse tracker value.
   * @param {string} type Type of tracker.
   * @param {unknown} value Value of tracker.
   * @returns {string}
   * @memberof TrackerSyncComponent
   */
  parseValue(type: string, value: unknown): string {
    if (type.toUpperCase() === 'BLOOD_PRESSURE') return JSON.stringify(value);
    if (value !== null && value !== undefined) {
      if (typeof value === 'object') {
        return JSON.stringify(value);
      }
      return toString(value);
    }
    return '';
  }

  /**
   * @description Query health data for a specific type and date range.
   * @param {string} type Tracker type.
   * @param {Date} startDate Start date.
   * @param {Date} endDate End date.
   * @returns {Observable<HealthData[]>}
   * @memberof TrackerSyncComponent
   */
  queryHealthData(
    type: string,
    startDate: Date,
    endDate: Date
  ): Observable<Array<HealthData>> {
    return from(
      this.health.query({
        dataType: type,
        startDate,
        endDate,
        limit: Number.MAX_VALUE
      })
    ).pipe(
      // map((data) => this.unique(data)),
      tap((data) => {
        this.logger.debug(`Queried ${data.length} records for ${type}`, {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        });
      })
    );
  }

  /**
   * @description Stop the sync process.
   * @memberof TrackerSyncComponent
   */
  stopSync(): void {
    if (this.isSyncing) {
      this.logger.info('Stopping sync process');
    }
    this.isSyncing = false;
    this.pauseSync$.next();
  }

  /**
   * @description Remove duplicates.
   * @param {Array<HealthData>} data Data.
   * @returns {Array<HealthData>}
   * @memberof TrackerSyncComponent
   */
  unique(data: Array<HealthData> = []): Array<HealthData> {
    const results = data.filter(
      (v, i) =>
        i ===
        data.findIndex(
          (o) =>
            o.endDate.toISOString() === v.endDate.toISOString() &&
            o.startDate.toISOString() === v.startDate.toISOString()
        )
    );

    const duplicate: Array<number> = [];
    for (const val of results) {
      const index = results.findIndex(
        (v) =>
          v.startDate.toISOString() === val.startDate.toISOString() &&
          DateTime.fromJSDate(v.endDate) > DateTime.fromJSDate(val.endDate)
      );
      if (index > -1) duplicate.push(index);
    }

    if (duplicate.length > 0) {
      return results.filter((_, i) => !duplicate.includes(i));
    }
    return results;
  }
}
